import React from 'react';
import Link from 'components/link';
import styles from './commercialFooterV2Style.module.scss';
import MailIcon from 'components/icon/mail-icon';
import PhoneIcon from 'components/icon/phone-icon';
import Image from 'next/image';
import { useTranslation } from 'next-i18next';
import { FooterConfig } from '@tenants/types/tenant';

export type ExpCommercialFooterProps = {
  config: FooterConfig;
};

export default function CommercialFooterV2({ config }: ExpCommercialFooterProps) {
  const { t } = useTranslation();
  const { organization } = config;
  const { facebookUrl, linkedinUrl, instagramUrl, youtubeUrl, email, phoneNumber, address } = organization;
  const { footerColumns } = config;
  const { column1 = [], column2 = [], column3 = [], bottom = []} = footerColumns;

  const firstRowLinks = [...column1, ...column2];
  return (
    <footer className={styles.footer} data-testid="exp-commercial-footer">
      <div className={styles.footerContent}>
        <nav className={styles.navigation}>
          <ul>
            {firstRowLinks.map(link => (
              // we use a plain <a> tag here because we want a full page navigation for the benifit of wordpress styling
              <li key={link.label}>
                <a href={link.link} key={link.label} rel="noreferrer">{t(link.label)}</a>
              </li>
            ))}
          </ul>
        </nav>

        <div className={styles.socialMedia}>
          <Link href={facebookUrl} aria-label="Facebook" target="_blank" rel="noreferrer">
            <Image src="/assets/images/facebook.svg" alt="Facebook" width={12} height={22} />
          </Link>
          <Link href={linkedinUrl} aria-label="LinkedIn" target="_blank" rel="noreferrer">
            <Image src="/assets/images/linkedin.svg" alt="LinkedIn" width={22} height={22} />
          </Link>
          <Link href={instagramUrl} aria-label="Instagram" target="_blank" rel="noreferrer">
            <Image src="/assets/images/instagram-outlined.svg" alt="Instagram" width={24} height={24} />
          </Link>
          <Link href={youtubeUrl} aria-label="YouTube" target="_blank" rel="noreferrer">
            <Image src="/assets/images/youtube-outlined.svg" alt="YouTube" width={24} height={24} />
          </Link>
        </div>

        <div className={styles.logo}>
          <Image src="/assets/images/exp-logo.svg" alt="eXp Realty" fill />
        </div>

        <div className={styles.footerBottom}>
          <div className={styles.contactInfo}>
            <address dangerouslySetInnerHTML={{ __html: address() }} />
            <p><PhoneIcon /> <Link href={`tel:${phoneNumber()}`}>{phoneNumber()}</Link></p>
            <p><MailIcon /> <Link href={`mailto:${email()}`}>{email()}</Link></p>
          </div>

          <div className={styles.legalLinks}>
            {column3.map(link => (
              <Link href={link.link} key={link.label} rel="noreferrer">{t(link.label)}</Link>
            ))}
          </div>

          <div className={styles.copyright}>
            <p>© {new Date().getFullYear()} eXp World Holdings, Inc. All Rights Reserved</p>
            <p>eXp Commercial is an eXp World Holdings, Inc. (Nasdaq:EXPI) company.</p>
          </div>

          <div className={styles.legalText}>
            <p>eXp Commercial is an eXp World Holdings, Inc. (Nasdaq: EXPI) company.</p>
            <p>eXp Commercial of California, Inc. | CA DRE # 01208047 | Jeffrey Peidon - Designated Broker</p>
            <p>Clifford Bogart - Designated Broker - TX</p>
            {bottom.slice(0, 2).map(section => (
              <p key={section.map(link => link.label).join('-')}>
                {section.map(link => (
                  <Link href={link.link} key={link.label} rel="noreferrer">{t(link.label)}</Link>
                ))}
              </p>
            ))}
          </div>
        </div>

      </div>
    </footer>
  );
}

